import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { InstallButton } from "@/components/InstallButton";
import { useTranslation, setLang } from "@/i18n";
import { MASCOT } from "@/lib/assets";
import {
  Robot, BookBookmark, Trophy, DeviceMobile,
  ArrowRight, CheckCircle, Star, Lightning, ShieldCheck, WhatsappLogo,
  MathOperations, Atom, Leaf, Translate, Globe, BookOpen, Code, ChartBar,
  Sparkle, Timer,
} from "@phosphor-icons/react";

const STUDENT_GENERAL = "https://images.pexels.com/photos/35305047/pexels-photo-35305047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const STUDENT_TECH = "https://images.pexels.com/photos/19344119/pexels-photo-19344119.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const STUDENT_SMILE = "https://images.unsplash.com/photo-1589483232748-515c025575bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxBZnJpY2FuJTIwdGVlbmFnZXJzJTIwc3R1ZGVudHMlMjBncm91cCUyMGhhcHB5fGVufDB8fHx8MTc4OTczMTI4OXww&ixlib=rb-4.1.0&q=85";

const PRESS = ["CRTV", "Cameroon Tribune", "Canal 2", "Le Jour", "Radio Balafon", "Équinoxe TV"];

// 👉 Collez ici l'URL de la vraie photo de M. Eloungou pour remplacer le monogramme « EL ».
const FOUNDER_PHOTO = "/founder.jpg";

const SUBJECTS = [
  { icon: MathOperations, name: "Mathématiques", tone: "#FF4B0A" },
  { icon: Atom, name: "Physique-Chimie", tone: "#05357E" },
  { icon: Leaf, name: "SVT", tone: "#10B981" },
  { icon: BookOpen, name: "Français", tone: "#FF4B0A" },
  { icon: Translate, name: "Anglais", tone: "#05357E" },
  { icon: Globe, name: "Histoire-Géo", tone: "#FFB800" },
  { icon: BookBookmark, name: "Philosophie", tone: "#05357E" },
  { icon: Code, name: "Informatique", tone: "#FF4B0A" },
  { icon: ChartBar, name: "Économie", tone: "#10B981" },
  { icon: Translate, name: "Espagnol", tone: "#FFB800" },
];

const COACH = [
  { img: MASCOT.book, title: "Des contenus fiables", desc: "Cours, fiches et quiz 100% conformes aux programmes officiels du MINESEC et du MINEDUB, conçus avec des enseignants camerounais." },
  { icon: Trophy, title: "Un univers motivant", desc: "XP, niveaux, badges, séries quotidiennes et classement national : réviser devient un jeu qui donne envie de continuer." },
  { icon: Robot, title: "Un parcours sur-mesure", desc: "Grâce à l'IA, le coach YAYA détecte vos points faibles et construit un plan de révision personnalisé, disponible 24h/24." },
];

const EXAM_FAMILIES = [
  { name: "Secondaire général", desc: "BEPC · Probatoire · Baccalauréat", img: STUDENT_GENERAL },
  { name: "Enseignement technique", desc: "CAP · BT · Bac Technique", img: STUDENT_TECH },
  { name: "Sous-système anglophone", desc: "GCE O Level · GCE A Level", img: STUDENT_SMILE },
];

const TESTIMONIALS = [
  { name: "Mme Mballa", role: "Maman d'un candidat au Baccalauréat", stars: 5, text: "Ma fille a gagné en confiance et en régularité. Les examens blancs corrigés par l'IA font toute la différence !" },
  { name: "M. Fotso", role: "Papa d'un élève de Probatoire", stars: 5, text: "Enfin une application pensée pour nos programmes camerounais. Le coach YAYA explique mieux que certains répétiteurs." },
  { name: "Ngono A.", role: "Élève en Terminale D", stars: 4, text: "Le carnet d'erreurs m'aide à revoir exactement ce que je rate. J'ai progressé en maths et en SVT." },
];

const PLANS = [
  { name: "Journalier", price: "500", period: "/ jour" },
  { name: "Hebdomadaire", price: "2 000", period: "/ semaine" },
  { name: "Mensuel", price: "5 000", period: "/ mois", popular: true },
  { name: "Annuel", price: "45 000", period: "/ an" },
];

function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} weight="fill" size={16} className={i < n ? "text-gold" : "text-slate-200"} />
      ))}
    </div>
  );
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const [founderOk, setFounderOk] = useState(true);
  const toggleLang = () => setLang(i18n.language === "fr" ? "en" : "fr");

  return (
    <div className="min-h-screen bg-white text-ink">
      {/* Compliance banner */}
      <div className="bg-forest px-4 py-2 text-center text-xs font-medium text-white sm:text-sm">
        <CheckCircle weight="fill" className="mr-1.5 inline text-gold" size={15} />
        100% conforme aux programmes officiels du Cameroun — MINESEC & MINEDUB
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-8 text-sm font-semibold text-ink/70 md:flex">
            <a href="#matieres" className="hover:text-terracotta">Matières</a>
            <a href="#examens" className="hover:text-terracotta">Examens</a>
            <a href="#coach" className="hover:text-terracotta">Le coach YAYA</a>
            <a href="#fondateur" className="hover:text-terracotta">Fondateur</a>
            <Link to="/a-propos" className="hover:text-terracotta">À propos</Link>
            <a href="#tarifs" className="hover:text-terracotta">Tarifs</a>
          </nav>
          <div className="flex items-center gap-2">
            <InstallButton className="hidden items-center gap-1.5 rounded-full border border-terracotta/40 px-3 py-2 text-xs font-bold text-terracotta transition hover:bg-terracotta/10 sm:inline-flex" />
            <button data-testid="landing-lang-toggle" onClick={toggleLang} className="rounded-full border border-border px-3 py-2 text-xs font-bold text-ink/70 transition hover:bg-sand">
              {i18n.language === "fr" ? "EN" : "FR"}
            </button>
            <Link to="/connexion" data-testid="nav-login" className="hidden rounded-full px-4 py-2 text-sm font-semibold text-ink transition hover:bg-sand sm:inline-block">{t("landing.login", "Connexion")}</Link>
            <Link to="/connexion?mode=register" data-testid="nav-signup" className="rounded-full bg-terracotta px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_22px_rgba(255,75,10,0.35)] transition hover:-translate-y-0.5 hover:bg-[#E03E00]">{t("landing.start", "Commencer")}</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-grid opacity-70" />
        <div className="pointer-events-none absolute -right-20 top-10 h-72 w-72 rounded-full bg-terracotta/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-forest/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-20">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-terracotta/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-terracotta">
              <Lightning weight="fill" size={14} /> N°1 de la préparation aux examens au Cameroun
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Révise, progresse,<br /><span className="text-terracotta">réussis</span> tes examens.
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
              Cours, quiz, examens blancs et un coach IA disponible 24h/24 — le tout conforme aux programmes officiels et pensé pour les élèves camerounais.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/connexion?mode=register" data-testid="hero-cta" className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-bold text-white shadow-[0_12px_30px_rgba(255,75,10,0.4)] transition hover:-translate-y-0.5 hover:bg-[#E03E00]">
                Démarrer gratuitement <ArrowRight weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="#coach" className="inline-flex items-center gap-2 rounded-full border-2 border-forest px-7 py-3 text-sm font-bold text-forest transition hover:bg-forest hover:text-white">
                Découvrir le coach
              </a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
              {[["+3 pts", "de moyenne gagnés"], ["4,8/5", "note des familles"], ["50 000+", "élèves accompagnés"]].map(([big, small]) => (
                <div key={big}>
                  <div className="font-display text-2xl font-extrabold text-forest">{big}</div>
                  <div className="text-xs text-slate-500">{small}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Phone mockup + mascot */}
          <div className="relative animate-fade-up" style={{ animationDelay: "0.12s" }}>
            <div className="relative mx-auto w-full max-w-[300px]">
              <div className="relative rounded-[2.5rem] border-[10px] border-forest bg-white p-3 shadow-[0_30px_70px_rgba(4,42,101,0.28)]">
                <div className="absolute left-1/2 top-3 h-1.5 w-16 -translate-x-1/2 rounded-full bg-forest/20" />
                <div className="rounded-[1.6rem] bg-cream p-4 pt-8">
                  <div className="flex items-center gap-2 text-xs font-bold text-forest"><Timer weight="fill" className="text-terracotta" /> Examen blanc · Maths</div>
                  <div className="mt-3 rounded-2xl bg-white p-4 shadow-sm">
                    <div className="text-[11px] font-semibold text-slate-500">Question 3 / 20</div>
                    <div className="mt-1 text-sm font-bold text-forest">Quelle est la dérivée de f(x) = x² ?</div>
                    <div className="mt-3 space-y-2">
                      {["f'(x) = 2x", "f'(x) = x", "f'(x) = 2"].map((opt, i) => (
                        <div key={opt} className={`rounded-xl px-3 py-2 text-xs font-medium ${i === 0 ? "bg-terracotta text-white" : "border border-border bg-white text-slate-600"}`}>{opt}</div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between rounded-2xl bg-forest px-3 py-2.5 text-white">
                    <span className="flex items-center gap-1.5 text-xs font-bold"><Trophy weight="fill" className="text-gold" /> +50 XP</span>
                    <span className="text-xs font-semibold text-white/80">Niveau 5</span>
                  </div>
                </div>
              </div>
              <img src={MASCOT.wave} alt="Mascotte YAYA" className="animate-float absolute -bottom-8 -left-16 hidden w-36 drop-shadow-xl sm:block" />
              <div className="absolute -right-6 top-8 flex items-center gap-2 rounded-2xl border border-border bg-white px-3 py-2 shadow-xl">
                <Robot weight="fill" className="text-terracotta" size={18} />
                <span className="text-xs font-bold text-forest">Coach IA en ligne</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Press strip */}
      <section className="border-y border-border bg-cream py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-slate-400">Ils parlent de YAYA</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {PRESS.map((p) => (
              <span key={p} className="font-display text-lg font-extrabold text-slate-300">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Subjects */}
      <section id="matieres" className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">Programme complet</span>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Toutes tes matières, toutes tes classes</h2>
            <p className="mt-4 text-slate-600">Du BEPC au Baccalauréat, en passant par le technique et le sous-système anglophone.</p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {SUBJECTS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.name} className="group flex cursor-default flex-col items-center rounded-2xl border border-border bg-white p-5 text-center transition hover:-translate-y-1 hover:border-terracotta/40 hover:shadow-lg">
                  <div className="grid h-14 w-14 place-items-center rounded-2xl transition group-hover:scale-110" style={{ background: `${s.tone}15`, color: s.tone }}>
                    <Icon size={28} weight="duotone" />
                  </div>
                  <div className="mt-3 text-sm font-bold text-forest">{s.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coach */}
      <section id="coach" className="bg-cream py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">Le coach YAYA</span>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">La révision devient simple et ludique</h2>
              <p className="mt-4 text-slate-600">Un compagnon d'apprentissage qui te motive, t'explique et te corrige — comme un répétiteur, mais dans ta poche.</p>
              <img src={MASCOT.point} alt="Coach YAYA" className="mt-6 w-44 lg:w-56" />
            </div>
            <div className="grid gap-5 sm:grid-cols-3 lg:col-span-2">
              {COACH.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="rounded-3xl border border-border bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                    {c.img ? (
                      <img src={c.img} alt="" className="h-16 w-16 object-contain" />
                    ) : (
                      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-terracotta/10 text-terracotta"><Icon size={28} weight="duotone" /></div>
                    )}
                    <h3 className="mt-4 font-display text-lg font-bold">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Exam families */}
      <section id="examens" className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">Chaque parcours</span>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Un accompagnement pour chaque examen</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {EXAM_FAMILIES.map((f) => (
              <Link key={f.name} to="/connexion?mode=register" className="group relative overflow-hidden rounded-3xl shadow-md transition hover:-translate-y-1 hover:shadow-2xl">
                <img src={f.img} alt={f.name} className="h-72 w-full object-cover transition duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/30 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6 text-white">
                  <h3 className="font-display text-xl font-extrabold">{f.name}</h3>
                  <p className="mt-1 text-sm text-white/80">{f.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-gold">Voir plus <ArrowRight weight="bold" size={16} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Feature band + AI chat */}
      <section className="bg-forest py-20 text-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Professeur IA</span>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Un enseignant rien que pour toi</h2>
            <p className="mt-4 text-white/75">Il connaît ton niveau, détecte tes difficultés, reformule les notions, crée des examens blancs et te corrige avec bienveillance.</p>
            <ul className="mt-6 space-y-3">
              {["Explications adaptées à ta classe d'examen", "Exercices et examens blancs générés à la demande", "Correction notée avec conseils de progression", "Mode hors-ligne pour les connexions lentes"].map((li) => (
                <li key={li} className="flex items-start gap-3"><CheckCircle weight="fill" className="mt-0.5 shrink-0 text-gold" /> <span className="text-white/85">{li}</span></li>
              ))}
            </ul>
            <Link to="/connexion?mode=register" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-ink transition hover:-translate-y-0.5 hover:brightness-105">
              Essayer le coach IA <ArrowRight weight="bold" />
            </Link>
          </div>
          <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur">
            <div className="space-y-4">
              <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-terracotta px-4 py-3 text-sm">Explique-moi simplement la dérivée d'une fonction.</div>
              <div className="max-w-[90%] rounded-2xl rounded-tl-sm bg-white px-4 py-3 text-sm text-ink">
                <div className="mb-1 flex items-center gap-2 font-bold text-terracotta"><Robot weight="fill" /> Coach YAYA</div>
                La dérivée mesure la <strong>vitesse de variation</strong> d'une fonction. Géométriquement, c'est la pente de la tangente à la courbe en un point. Prenons un exemple…
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="flex items-center justify-center gap-2">
              <Stars n={5} />
              <span className="font-display text-2xl font-extrabold text-forest">4,8/5</span>
            </div>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Les familles nous font confiance</h2>
            <p className="mt-3 text-slate-600">Plus de 15 000 avis de parents et d'élèves partout au Cameroun.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((r) => (
              <div key={r.name} className="rounded-3xl border border-border bg-white p-7 shadow-sm">
                <Stars n={r.stars} />
                <p className="mt-4 text-sm leading-relaxed text-slate-700">“{r.text}”</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="grid h-11 w-11 place-items-center rounded-full bg-terracotta/10 font-display font-extrabold text-terracotta">{r.name[0]}</div>
                  <div>
                    <div className="text-sm font-bold text-forest">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App download + WhatsApp */}
      <section className="bg-cream py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">Installe & abonne-toi</span>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Une application, zéro complication</h2>
            <p className="mt-4 text-slate-600">Installe YAYA directement depuis ton navigateur (PWA), fonctionne même en connexion lente et hors-ligne. Abonne-toi en un message WhatsApp, activation par notre équipe.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/connexion?mode=register" className="inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-[#05357E]">
                <DeviceMobile weight="fill" /> Installer l'app
              </Link>
              <a href="https://wa.me/237659946824" target="_blank" rel="noreferrer" data-testid="landing-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:brightness-105">
                <WhatsappLogo weight="fill" /> S'abonner via WhatsApp
              </a>
            </div>
            <div className="mt-6 flex items-center gap-6 text-sm text-slate-600">
              <span className="inline-flex items-center gap-2"><ShieldCheck weight="fill" className="text-forest" /> Activation manuelle sécurisée</span>
              <span className="inline-flex items-center gap-2"><Sparkle weight="fill" className="text-terracotta" /> Dès 500 FCFA</span>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute -inset-6 rounded-full bg-terracotta/10 blur-2xl" />
              <img src={MASCOT.rocket} alt="Réussite YAYA" className="animate-float relative w-64 rounded-3xl sm:w-80" />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="tarifs" className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">Tarifs</span>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Des offres pour chaque budget</h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-600">Paiement par Mobile Money via WhatsApp. Activation par notre équipe, annulez quand vous voulez.</p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PLANS.map((p) => (
              <div key={p.name} className={`relative rounded-3xl border p-7 transition hover:-translate-y-1 ${p.popular ? "border-terracotta bg-white shadow-[0_18px_50px_rgba(255,75,10,0.18)]" : "border-border bg-white"}`}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-terracotta px-3 py-1 text-xs font-bold text-white">Populaire</span>}
                <div className="text-sm font-bold uppercase tracking-wide text-slate-500">{p.name}</div>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-display text-3xl font-extrabold text-forest">{p.price}</span>
                  <span className="mb-1 text-sm text-slate-500">FCFA{p.period}</span>
                </div>
                <Link to="/connexion?mode=register" className={`mt-6 block rounded-full py-2.5 text-center text-sm font-bold transition ${p.popular ? "bg-terracotta text-white hover:bg-[#E03E00]" : "border-2 border-forest text-forest hover:bg-forest hover:text-white"}`}>
                  Choisir
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-slate-500">Abonnements famille, licences établissements & ONG disponibles sur demande.</p>
        </div>
      </section>

      {/* Founder */}
      <section id="fondateur" className="bg-cream py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 rounded-[2rem] border border-border bg-white p-8 shadow-sm sm:p-12 lg:grid-cols-[auto_1fr]">
            <div className="flex flex-col items-center text-center">
              <div className="relative">
                {FOUNDER_PHOTO && founderOk ? (
                  <img src={FOUNDER_PHOTO} alt="M. Eloungou Etienne Landry" onError={() => setFounderOk(false)} className="h-64 w-48 rounded-3xl border-4 border-white object-cover object-top shadow-xl" />
                ) : (
                  <div className="grid h-36 w-36 place-items-center rounded-full bg-gradient-to-br from-forest to-[#05357E] font-display text-4xl font-extrabold text-white shadow-lg">EL</div>
                )}
                <img src={MASCOT.point} alt="" className="absolute -bottom-4 -right-6 w-16 drop-shadow-lg" />
              </div>
              <div className="mt-5 font-display text-lg font-extrabold text-forest">M. Eloungou Etienne Landry</div>
              <div className="text-sm font-semibold text-terracotta">Fondateur du groupe YAYA · « M. YAYA »</div>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-terracotta">Le mot du fondateur</span>
              <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Une plateforme née d'une conviction</h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                M. Eloungou Etienne Landry, affectueusement surnommé « M. YAYA », est le fondateur du groupe YAYA.
                Passionné d'éducation et profondément attaché à la réussite de la jeunesse camerounaise, il place
                l'accès au savoir au cœur de son engagement. Convaincu que chaque élève mérite les mêmes chances de
                réussite, il a fait de l'innovation pédagogique et de l'accompagnement scolaire le combat d'une vie.
                Attentif aux réalités du terrain, des familles et de nos salles de classe, c'est cette proximité qui
                inspire chacune de ses décisions au service d'une jeunesse mieux préparée.
              </p>
              <blockquote className="mt-5 border-l-4 border-terracotta pl-4">
                <p className="italic leading-relaxed text-forest">
                  « J'ai créé YAYA EXAM ACADEMY pour que la réussite ne dépende plus du lieu où l'on naît ni des moyens de
                  sa famille. En mettant un professeur et un coach dans chaque poche — même hors connexion — je veux
                  redonner à chaque candidat le pouvoir de réussir aujourd'hui et de construire demain. »
                </p>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-terracotta px-8 py-16 text-center text-white">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
          <div className="absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-white/10" />
          <div className="relative">
            <img src={MASCOT.celebrate} alt="" className="animate-float mx-auto w-32 drop-shadow-xl" />
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-extrabold sm:text-4xl">Prêt à transformer tes révisions ?</h2>
            <p className="mx-auto mt-4 max-w-xl text-white/90">Rejoins les milliers de candidats qui se préparent intelligemment avec YAYA EXAM ACADEMY.</p>
            <Link to="/connexion?mode=register" data-testid="cta-signup" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-bold text-terracotta transition hover:-translate-y-0.5 hover:bg-cream">
              Créer mon compte gratuit <ArrowRight weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-white py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:px-6 md:flex-row lg:px-8">
          <Logo />
          <p className="text-sm text-slate-500">© 2026 YAYA EXAM ACADEMY — Réussir aujourd'hui. Construire demain.</p>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <Link to="/a-propos" className="hover:text-terracotta">À propos</Link>
            <Link to="/contact" className="hover:text-terracotta">Contact</Link>
            <a href="https://wa.me/237659946824" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#25D366]"><WhatsappLogo weight="fill" /> Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
